import t from "@ext/localization/locale/translate";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

// The diff engine compares marks by name only, so an `href` change with the same text shows no inline change.
export const linkChangesKey = new PluginKey<DecorationSet>("diff-link-changes");

export interface LinkRun {
	text: string;
	href: string;
	target: string;
	/** Relative to the node content start. */
	from: number;
	to: number;
}

export const linkRuns = (node: Node): LinkRun[] => {
	const runs: LinkRun[] = [];
	node.descendants((child, pos) => {
		if (!child.isText) return true;
		const link = child.marks.find((mark) => mark.type.name === "link");
		if (!link) return false;
		const href = String(link.attrs.href ?? "");
		const resourcePath = link.attrs.resourcePath ? String(link.attrs.resourcePath) : "";
		const target = resourcePath ? `${resourcePath}${link.attrs.hash ?? ""}` : href;
		runs.push({ text: child.text ?? "", href, target, from: pos, to: pos + child.nodeSize });
		return false;
	});
	return runs;
};

export interface LinkPairs {
	changed: { run: LinkRun; old: LinkRun }[];
	removed: LinkRun[];
	added: LinkRun[];
}

export const pairLinks = (before: Node, after: Node): LinkPairs => {
	const old = linkRuns(before);
	const now = linkRuns(after);
	const used = new Set<number>();
	const changed: LinkPairs["changed"] = [];
	const added: LinkRun[] = [];

	for (const run of now) {
		const index = old.findIndex((candidate, position) => !used.has(position) && candidate.text === run.text);
		if (index < 0) {
			added.push(run);
			continue;
		}
		used.add(index);
		if (old[index].target !== run.target) changed.push({ run, old: old[index] });
	}

	return { changed, removed: old.filter((_, position) => !used.has(position)), added };
};

export const linkHint = (oldHref: string, newHref: string) =>
	`${t("diff.link.changed")}\n${t("diff.link.was")}: ${oldHref}\n${t("diff.link.now")}: ${newHref}`;

const decorationsFor = (changes: DiffChange[], doc: Node) => {
	const size = doc.content.size;
	const decorations = changes
		.filter((change) => change.type === "modified" && change.nodeA && change.nodeB && change.posB !== undefined)
		.flatMap((change) => {
			const base = (change.posB as number) + 1;
			return pairLinks(change.nodeA as Node, change.nodeB as Node).changed.map(({ run, old }) => ({
				from: base + run.from,
				to: base + run.to,
				old,
				run,
			}));
		})
		.filter(({ from, to }) => from >= 0 && to <= size && from < to)
		.map(({ from, to, old, run }) =>
			Decoration.inline(from, to, {
				class: "diff-mark-changed diff-link-changed",
				"data-old-href": old.target,
				"data-new-href": run.target,
				title: linkHint(old.target, run.target),
			}),
		);
	return decorations.length ? DecorationSet.create(doc, decorations) : DecorationSet.empty;
};

const linkChanges = (getChanges: () => DiffChange[] | undefined) =>
	new Plugin<DecorationSet>({
		key: linkChangesKey,
		state: {
			init: (_, state) => decorationsFor(getChanges() ?? [], state.doc),
			apply: (tr, set) =>
				tr.getMeta(linkChangesKey) ? decorationsFor(getChanges() ?? [], tr.doc) : set.map(tr.mapping, tr.doc),
		},
		props: {
			decorations(state) {
				return linkChangesKey.getState(state);
			},
		},
	});

export default linkChanges;
